import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const iconButtonVariants = cva(
  [
    'inline-flex items-center justify-center shrink-0 rounded-sm',
    'transition-colors duration-[--duration-micro] ease-[--ease-out-quint]',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        ghost: 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
        secondary:
          'bg-surface-raised text-text-primary border border-border-default hover:bg-surface-hover',
        primary: 'bg-primary text-text-on-primary hover:bg-primary-hover',
        danger: 'text-danger-fg hover:bg-danger-subtle',
      },
      size: {
        sm: 'size-7 [&_svg]:size-4',
        md: 'size-9 [&_svg]:size-4',
        lg: 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
);

export interface IconButtonProps
  extends
    Omit<ComponentPropsWithoutRef<'button'>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  /**
   * Required. An icon-only control with no accessible name is invisible to
   * screen readers, so the type system insists on one.
   */
  label: string;
  icon: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, label, icon, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      {icon}
    </button>
  );
});
